import React, { useMemo } from 'react';
import {
  BalanceAdjustmentIcon,
  Box,
  CarrierConfigOutlineIcon,
  CensusIcon,
  ContributionIcon,
  EnrollmentIcon,
  ErrorIcon,
  Inscription,
  MixIcon,
  Preloader,
  Text,
  Unknown,
} from '@common-fe/common-fe';
import dayjs from 'dayjs';
import styled from 'styled-components';

import { DATE_MASK_TIME, MINIMIZE_DATE_FORMAT } from '@/common/constants';
import theme from '@/styles/theme';
import { IFileTypeDtoFileTypeEnum } from '@/types';

import { FileDtoWithMediaSource, FileStatus } from '../FileManager.constants';
import useFilesListQuery, { FilesQueryParams } from '../queries/useFilesList.query';
import useFilesProcess from '../queries/useFilesProcess.query';

import useBacklightStop from './useBacklightStop';
import useUpdatingFiles from './useUpdatingFiles';

export const HIDDEN_UPLOADING_WAY = 'UI';

const StyledEnrollmentIcon = styled(EnrollmentIcon)`
  width: 24px;
  height: 24px;
`;

export const ERROR_TEXT = "Oops! We couldn't upload the file.";

const isDate = (date: string) => dayjs(date).isValid();

const getDate = (date: string) => {
  if (!isDate(date)) {
    return date;
  }

  return dayjs(date).format(MINIMIZE_DATE_FORMAT);
};
const getTime = (date: string) => {
  if (!isDate(date)) {
    return date;
  }

  return dayjs(date).format(DATE_MASK_TIME).replace(/_/, 'at');
};

export const getAvatar = (type: string, status?: string, isDisabled?: boolean) => {
  if (status === FileStatus.Processing) {
    return <Preloader size={18} />;
  }

  if (type === IFileTypeDtoFileTypeEnum.CENSUS) {
    return (
      <CensusIcon
        color={isDisabled ? theme.colors.noAssignee : theme.colors.fileManagerTypes.cencusType}
        size="24px"
      />
    );
  }

  if (type === IFileTypeDtoFileTypeEnum.ENROLLMENT) {
    return (
      <StyledEnrollmentIcon
        color={isDisabled ? theme.colors.noAssignee : theme.colors.fileManagerTypes.enrollmentType}
      />
    );
  }

  if (type === IFileTypeDtoFileTypeEnum.CONTRIBUTION) {
    return (
      <ContributionIcon
        color={isDisabled ? theme.colors.noAssignee : theme.colors.fileManagerTypes.contributionType}
        size="24px"
      />
    );
  }

  if (type === 'Error' || type === 'ERROR') {
    return (
      <ErrorIcon
        color={isDisabled ? theme.colors.noAssignee : theme.colors.fileManagerTypes.unknownType}
        size="24px"
      />
    );
  }

  if (type === IFileTypeDtoFileTypeEnum.UNKNOWN) {
    return (
      <Unknown
        color={isDisabled ? theme.colors.noAssignee : theme.colors.fileManagerTypes.unknownType}
        size="24px"
      />
    );
  }
  if (type === IFileTypeDtoFileTypeEnum.DEPENDENT) {
    return (
      <CensusIcon
        color={isDisabled ? theme.colors.noAssignee : theme.colors.fileManagerTypes.cencusType}
        size="24px"
      />
    );
  }
  if (type === IFileTypeDtoFileTypeEnum.MIXED) {
    return (
      <MixIcon
        color={isDisabled ? theme.colors.noAssignee : theme.colors.fileManagerTypes.mixType}
        size="24px"
      />
    );
  }

  if (type === IFileTypeDtoFileTypeEnum.CARRIER) {
    return (
      <CarrierConfigOutlineIcon
        color={isDisabled ? theme.colors.noAssignee : theme.colors.fileManagerTypes.carrierType}
      />
    );
  }
  if (type === IFileTypeDtoFileTypeEnum.BALANCE_ADJUSTMENT) {
    return (
      <BalanceAdjustmentIcon
        color={isDisabled ? theme.colors.noAssignee : theme.colors.fileManagerTypes.adjustment}
      />
    );
  }

  return <Preloader size={18} />;
};

interface Props {
  names: string[];
}

export const ManyNames: React.FC<Props> = ({ names }) => {
  if (!names.length) {
    return null;
  }

  const joinedNames = names.length > 1 ? names.length - 1 : '';

  return (
    <Inscription style={{ display: 'flex' }} title={names.join(', ')} margin={{ right: 'spacing12' }}>
      <Inscription hasEllipsisMode>{names[0]}</Inscription>
      {joinedNames && ', '}
      {joinedNames && <Inscription color="textAccent">+{joinedNames}</Inscription>}
    </Inscription>
  );
};

export const FormattedDate: React.FC<{ from: string; to: string }> = ({ from, to }) => {
  if (!from && !to) {
    return null;
  }

  return (
    <Inscription>
      {from ? <Inscription title={getTime(from)}>{getDate(from)}</Inscription> : null}
      {!!from && <Text color="textDisabled"> / </Text>}
      {to ? <Inscription title={getTime(to)}>{getDate(to)}</Inscription> : <Inscription>?</Inscription>}
    </Inscription>
  );
};

export const UploadedBy: React.FC<{ name: string; way: string }> = ({ name, way }) => {
  const hasWay = way && way !== HIDDEN_UPLOADING_WAY;

  return (
    <Box direction="row" align="center" justify="between">
      <Inscription title={name} hasEllipsisMode {...(hasWay ? {} : { margin: { right: 'xxs' } })}>
        {name}
      </Inscription>

      {hasWay && (
        <Box
          background={{ color: 'border1' }}
          width={{ min: '24px' }}
          height={{ min: '18px' }}
          round="spacing4"
          margin={{ left: 'spacing4', right: '17px' }}
          align="center"
        >
          <Inscription size="xsmall" color="textBody" weight={500}>
            {way}
          </Inscription>
        </Box>
      )}
    </Box>
  );
};

const useFilesList = (params: FilesQueryParams) => {
  const { uploadedFiles } = params;
  const { files, isLoading, total } = useFilesListQuery(params);
  const allFiles = useMemo(() => [...uploadedFiles, ...files], [uploadedFiles, files]);
  const { onFileProcess, processedFiles } = useFilesProcess(allFiles as FileDtoWithMediaSource[], isLoading);
  const refetchedFiles = useUpdatingFiles(processedFiles, isLoading);
  const updatedFiles = useBacklightStop(refetchedFiles);
  const isEmpty = useMemo(() => updatedFiles.length === 0, [updatedFiles.length]);

  return {
    isEmpty,
    list: files,
    isLoading,
    total,
    updatedFiles,
    onFileProcess,
  };
};

export default useFilesList;
